import { useMemo } from 'react'
import { useQuery } from 'react-query'
import type { AxiosError } from 'axios'
import {
  RECIPES_BATCH_LIMIT,
  fetchCalculatorRecipes,
} from '../api/calculatorApi'
import type { CalculatorRecipe, CalculatorRecipesResponse } from '../types'
import { CALCULATOR_KEYS } from './calculatorKeys'

/**
 * Составы запрошенных рецептов.
 *
 * Батч ограничен 20 идентификаторами, поэтому запрашиваем только те карточки,
 * что действительно открыты, а не весь каталог. Ответ раскладываем по `card_id`:
 * карточки со снятым калькулятором из него выпадают, и позиционное
 * сопоставление сдвинуло бы состав одного рецепта на другой.
 */
export const useCalculatorRecipesQuery = (
  cardIds: readonly number[],
  enabled = true
) => {
  const ids = useMemo(
    () => [...cardIds].slice(0, RECIPES_BATCH_LIMIT).sort((a, b) => a - b),
    [cardIds]
  )

  const query = useQuery<CalculatorRecipesResponse, AxiosError>(
    CALCULATOR_KEYS.recipes(ids),
    () => fetchCalculatorRecipes(ids),
    {
      enabled: enabled && ids.length > 0,
      staleTime: 0,
      refetchOnWindowFocus: false,
      retry: false,
    }
  )

  const byCardId = useMemo(
    () =>
      new Map(
        (query.data?.recipes ?? []).map(recipe => [recipe.card_id, recipe])
      ),
    [query.data]
  )

  return { ...query, byCardId }
}

/** Один рецепт. Та же ручка, просто батч из одного идентификатора. */
export const useCalculatorRecipeQuery = (cardId: number | null) => {
  const ids = useMemo(() => (cardId === null ? [] : [cardId]), [cardId])
  const query = useCalculatorRecipesQuery(ids, cardId !== null)

  const recipe: CalculatorRecipe | undefined =
    cardId === null ? undefined : query.byCardId.get(cardId)

  // Ответ пришёл, а рецепта в нём нет — калькулятор у карточки сняли с
  // публикации. Это не ошибка запроса, но и состава не будет.
  const isUnpublished = query.isSuccess && cardId !== null && !recipe

  return { ...query, recipe, isUnpublished }
}
